const ZOOM_STEPS = [0.8, 1.2, 1.6, 2];

if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends ModalDialog {
    constructor() {
      super();

      this._images = [];
      this._currentIndex = 0;
      this._dragging = false;
      this._panAbort = null;
      this._renderAbort = null;
      this._scale = 1;
      this._tx = 0;
      this._ty = 0;
      this._imgW = 1000;
      this._imgH = 0;
      this._mql = window.matchMedia('(max-width: 749px)');

      this._content = this.querySelector('.product-media-modal__content');
      this._prevBtn = this.querySelector('.product-media-modal__nav-btn--prev');
      this._nextBtn = this.querySelector('.product-media-modal__nav-btn--next');
      this._zoomInBtn = this.querySelector('.product-media-modal__zoom-btn--in');
      this._zoomOutBtn = this.querySelector('.product-media-modal__zoom-btn--out');

      [this._content, this._prevBtn, this._nextBtn, this._zoomInBtn, this._zoomOutBtn].forEach((el) => {
        el?.addEventListener('pointerup', (e) => e.stopPropagation());
      });

      this._prevBtn?.addEventListener('click', (e) => { e.stopPropagation(); this.navigate(-1); });
      this._nextBtn?.addEventListener('click', (e) => { e.stopPropagation(); this.navigate(1); });
      this._zoomInBtn?.addEventListener('click', (e) => { e.stopPropagation(); this._stepZoom(1); });
      this._zoomOutBtn?.addEventListener('click', (e) => { e.stopPropagation(); this._stepZoom(-1); });
    }

    show(opener) {
      super.show(opener);
      this._images = this._collectImages(opener);
      const clickedId = opener.getAttribute('data-media-id');
      this._currentIndex = this._images.findIndex(
        (img) => img.slideMediaId.endsWith('-' + clickedId)
      );
      if (this._currentIndex < 0) this._currentIndex = 0;
      this._render();
      this._updateNav();
      this._enterZoom();
    }

    _collectImages(opener) {
      const slider = opener.closest('slider-component');
      if (!slider) return [];
      const slides = slider.querySelectorAll(
        '.product__media-item:not(.product__media-item--variant), .product__media-item.is-variant-active'
      );
      return [...slides].map((slide) => {
        const img = slide.querySelector('img');
        if (!img) return null;
        const src = img.currentSrc || img.src;
        const srcFull = slide.querySelector('[data-full-src]')?.dataset.fullSrc || (() => {
          try {
            const url = new URL(src, window.location.href);
            url.searchParams.set('width', '2000');
            return url.toString();
          } catch (_) { return src; }
        })();
        return { slideMediaId: slide.dataset.mediaId || '', src, srcFull, alt: img.alt || '', nw: img.naturalWidth, nh: img.naturalHeight };
      }).filter(Boolean);
    }

    _render() {
      if (!this._content) return;

      this._renderAbort?.abort();
      this._renderAbort = new AbortController();
      this._panAbort?.abort();
      this._panAbort = null;

      const data = this._images[this._currentIndex];
      if (!data) return;

      this._content.innerHTML = '';
      this._content.classList.remove('is-dragging');
      this._dragging = false;

      const img = document.createElement('img');
      img.src = data.src;
      img.alt = data.alt;
      img.className = 'product-media-modal__img';
      img.draggable = false;
      img.style.width = '1000px';
      this._imgW = 1000;
      if (data.nw > 0 && data.nh > 0) {
        this._imgH = Math.round(1000 * data.nh / data.nw);
        img.style.height = this._imgH + 'px';
      } else {
        this._imgH = 0;
      }
      this._content.appendChild(img);
      this._img = img;

      this._content.addEventListener('click', (e) => {
        if (this._dragging) return;
        if (this._mql.matches) return;
        if (this._isClickOnImg(e)) return;
        this.hide();
      }, { signal: this._renderAbort.signal });
    }

    _isClickOnImg(e) {
      if (!this._img) return false;
      const r = this._img.getBoundingClientRect();
      return e.clientX >= r.left && e.clientX <= r.right &&
             e.clientY >= r.top  && e.clientY <= r.bottom;
    }

    _enterZoom() {
      if (!this._content || !this._img) return;
      const data = this._images[this._currentIndex];
      if (data && data.srcFull !== this._img.src) {
        const preload = new Image();
        preload.onload = () => {
          if (!this._img) return;
          this._img.src = data.srcFull;
        };
        preload.src = data.srcFull;
      }
      this._setupPan();
    }

    _applyTransform() {
      if (!this._img) return;
      this._img.style.transform = `translate(${this._tx}px, ${this._ty}px) scale(${this._scale})`;
    }

    _clampTranslate() {
      if (!this._content) return;
      const imgH = this._imgH || this._img?.offsetHeight || 0;
      const maxTx = Math.max(0, (this._imgW * this._scale - this._content.clientWidth) / 2);
      const maxTy = imgH > 0 ? Math.max(0, (imgH * this._scale - this._content.clientHeight) / 2) : 0;
      this._tx = Math.max(-maxTx, Math.min(maxTx, this._tx));
      this._ty = Math.max(-maxTy, Math.min(maxTy, this._ty));
    }

    _setZoom(newScale, fx, fy) {
      newScale = Math.max(0.8, Math.min(2, newScale));
      if (fx !== undefined && fy !== undefined && this._content) {
        // content is position:fixed full-viewport so left/top are always 0
        const cx = this._content.clientWidth / 2;
        const cy = this._content.clientHeight / 2;
        const ratio = newScale / this._scale;
        this._tx = (fx - cx) + (this._tx - (fx - cx)) * ratio;
        this._ty = (fy - cy) + (this._ty - (fy - cy)) * ratio;
      }
      this._scale = newScale;
      this._clampTranslate();
      this._applyTransform();
    }

    _animatedZoom(newScale, fx, fy) {
      if (!this._img) return;
      this._img.classList.add('is-zoom-animating');
      this._setZoom(newScale, fx, fy);
      this._img.addEventListener('transitionend', () => {
        this._img?.classList.remove('is-zoom-animating');
      }, { once: true });
    }

    _cycleZoom(fx, fy) {
      const next = ZOOM_STEPS.find(s => s > this._scale + 0.05);
      this._animatedZoom(next !== undefined ? next : ZOOM_STEPS[0], fx, fy);
    }

    _stepZoom(dir) {
      const next = dir > 0
        ? ZOOM_STEPS.find(s => s > this._scale + 0.05)
        : [...ZOOM_STEPS].reverse().find(s => s < this._scale - 0.05);
      if (next !== undefined) this._animatedZoom(next);
    }

    _setupPan() {
      this._panAbort?.abort();
      this._panAbort = new AbortController();
      const signal = this._panAbort.signal;
      const content = this._content;
      const IGNORE = '.product-media-modal__nav-btn, .product-media-modal__toggle, .product-media-modal__zoom-controls';

      this._scale = this._mql.matches ? 0.8 : 1.6;
      this._tx = 0;
      this._ty = 0;
      this._applyTransform();

      const active = new Map();
      let panStartTx = 0, panStartTy = 0, panPointerId = null;
      let pinchStartDist = 0, pinchStartScale = 1, pinchStartTx = 0, pinchStartTy = 0, pinchMidX = 0, pinchMidY = 0;
      let lastTap = 0, tapX = 0, tapY = 0;
      let rafId = null;

      const scheduleApply = () => {
        if (rafId) return;
        rafId = requestAnimationFrame(() => {
          rafId = null;
          this._applyTransform();
        });
      };

      const onDown = (e) => {
        if (e.target.closest(IGNORE)) return;
        try { content.setPointerCapture(e.pointerId); } catch (_) {}
        active.set(e.pointerId, { x: e.clientX, y: e.clientY });

        if (active.size === 1) {
          const now = Date.now();
          if (now - lastTap < 300 && Math.abs(e.clientX - tapX) < 40 && Math.abs(e.clientY - tapY) < 40) {
            this._cycleZoom(e.clientX, e.clientY);
            lastTap = 0;
          } else {
            lastTap = now;
            tapX = e.clientX;
            tapY = e.clientY;
          }
          panStartTx = this._tx - e.clientX;
          panStartTy = this._ty - e.clientY;
          panPointerId = e.pointerId;
          this._dragging = false;
        } else if (active.size === 2) {
          panPointerId = null;
          const pts = [...active.values()];
          pinchStartDist = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
          pinchStartScale = this._scale;
          pinchStartTx = this._tx;
          pinchStartTy = this._ty;
          pinchMidX = (pts[0].x + pts[1].x) / 2 - content.clientWidth / 2;
          pinchMidY = (pts[0].y + pts[1].y) / 2 - content.clientHeight / 2;
        }
      };

      const onMove = (e) => {
        if (!active.has(e.pointerId)) return;
        active.set(e.pointerId, { x: e.clientX, y: e.clientY });
        e.preventDefault();

        if (active.size === 1 && e.pointerId === panPointerId) {
          const newTx = e.clientX + panStartTx;
          const newTy = e.clientY + panStartTy;
          if (!this._dragging && (Math.abs(newTx - this._tx) > 3 || Math.abs(newTy - this._ty) > 3)) {
            this._dragging = true;
            content.classList.add('is-dragging');
          }
          if (this._dragging) {
            this._tx = newTx;
            this._ty = newTy;
            this._clampTranslate();
            scheduleApply();
          }
        } else if (active.size === 2) {
          const pts = [...active.values()];
          const dist = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
          const newScale = Math.max(0.8, Math.min(2, pinchStartScale * dist / pinchStartDist));
          const ratio = newScale / pinchStartScale;
          this._tx = pinchMidX + (pinchStartTx - pinchMidX) * ratio;
          this._ty = pinchMidY + (pinchStartTy - pinchMidY) * ratio;
          this._scale = newScale;
          this._clampTranslate();
          scheduleApply();
        }
      };

      const onUp = (e) => {
        active.delete(e.pointerId);
        if (active.size === 0) {
          content.classList.remove('is-dragging');
          requestAnimationFrame(() => { this._dragging = false; });
          panPointerId = null;
        } else if (active.size === 1) {
          const [pid, pt] = [...active.entries()][0];
          panStartTx = this._tx - pt.x;
          panStartTy = this._ty - pt.y;
          panPointerId = pid;
        }
      };

      content.addEventListener('pointerdown', onDown, { signal });
      content.addEventListener('pointermove', onMove, { signal, passive: false });
      content.addEventListener('pointerup', onUp, { signal });
      content.addEventListener('pointercancel', onUp, { signal });
    }

    navigate(direction) {
      if (this._images.length < 2) return;
      this._currentIndex = (this._currentIndex + direction + this._images.length) % this._images.length;
      this._render();
      this._updateNav();
      this._enterZoom();
    }

    _updateNav() {
      const show = this._images.length > 1;
      if (this._prevBtn) this._prevBtn.style.display = show ? '' : 'none';
      if (this._nextBtn) this._nextBtn.style.display = show ? '' : 'none';
    }

    hide() {
      this._panAbort?.abort();
      this._panAbort = null;
      this._renderAbort?.abort();
      this._renderAbort = null;
      this._dragging = false;
      if (this._content) {
        this._content.innerHTML = '';
        this._content.classList.remove('is-dragging');
      }
      this._img = null;
      super.hide();
    }
  });
}
